import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import './Checklist.css';
import { checklistService } from './checklistService';
import MainLoader from '../../../shared/components/MainLoader';
import MobileFooterNav from '../../../shared/components/MobileFooterNav';
import backIcon from '../../../shared/assets/icons/back.png';
import { IoArrowUndoOutline } from 'react-icons/io5';
import { MdCheckCircle, MdCancel, MdExpandMore, MdExpandLess } from 'react-icons/md';

const Checklist = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expandedId, setExpandedId] = useState(null);
  const [answers, setAnswers] = useState({});
  const [answersLoading, setAnswersLoading] = useState({});

  useEffect(() => {
    const fetch = async () => {
      setLoading(true);
      setError(null);
      try {
        const data = await checklistService.fetchChecklist(id);
        if (data?.success) {
          setItems(data.data || []);
        } else {
          setItems([]);
        }
      } catch (err) {
        console.error('Error fetching checklist:', err);
        setError('Failed to load checklist.');
      } finally {
        setLoading(false);
      }
    };
    fetch();
  }, [id]);

  const handleCardClick = async (item) => {
    const checklistId = item.id;
    if (expandedId === checklistId) {
      setExpandedId(null);
      return;
    }
    setExpandedId(checklistId);
    if (answers[checklistId]) return;

    setAnswersLoading((prev) => ({ ...prev, [checklistId]: true }));
    try {
      const data = await checklistService.fetchAnswers(id, checklistId);
      if (data?.success && data.data?.length > 0) {
        setAnswers((prev) => ({ ...prev, [checklistId]: data.data[0] }));
      } else {
        setAnswers((prev) => ({ ...prev, [checklistId]: null }));
      }
    } catch (err) {
      console.error('Error fetching answers:', err);
      setAnswers((prev) => ({ ...prev, [checklistId]: null }));
    } finally {
      setAnswersLoading((prev) => ({ ...prev, [checklistId]: false }));
    }
  };

  const getStatusLabel = (status) => (status === 0 ? 'Pending' : 'Completed');
  const getStatusClass = (status) => (status === 0 ? 'pending' : 'completed');

  const renderAnswers = (checklistId) => {
    if (answersLoading[checklistId]) {
      return <div className="checklist-answers-loading"><div className="checklist-answers-spinner" /></div>;
    }
    const answerData = answers[checklistId];
    if (!answerData) {
      return <p className="checklist-no-answers">No answers submitted yet.</p>;
    }
    return (
      <div className="checklist-answers">
        <p className="checklist-answers-meta">
          Submitted: {answerData.submitted_at}
        </p>
        <ul className="checklist-answer-list">
          {answerData.answer_list.map((ans) => (
            <li key={ans.item_id} className="checklist-answer-item">
              <span className={`checklist-answer-icon ${ans.pass === 1 ? 'pass' : 'fail'}`}>
                {ans.pass === 1
                  ? <MdCheckCircle size={18} />
                  : <MdCancel size={18} />}
              </span>
              <span className="checklist-answer-text">{ans.item_type}</span>
              {ans.comments && (
                <span className="checklist-answer-comment">{ans.comments}</span>
              )}
            </li>
          ))}
        </ul>
      </div>
    );
  };

  const renderContent = () => {
    if (loading) return <MainLoader />;
    if (error || items.length === 0) {
      return (
        <div className="checklist-empty">
          <div className="checklist-empty-icon">📋</div>
          <div className="checklist-empty-text">{error || 'No checklist items found'}</div>
        </div>
      );
    }
    return (
      <div className="checklist-grid">
        {items.map((item) => (
          <div key={item.id} className="checklist-card" onClick={() => handleCardClick(item)}>
            <div className="checklist-card-top">
              <div className="checklist-card-left">
                <p className="checklist-card-title">{item.title}</p>
                <p className="checklist-card-header">{item.header}</p>
                <p className="checklist-card-meta">
                  {item.category_name} · {item.make_name} · {item.model_name}
                </p>
              </div>
              <div className="checklist-card-right">
                <span className={`checklist-status-badge ${getStatusClass(item.status)}`}>
                  {getStatusLabel(item.status)}
                </span>
                {expandedId === item.id
                  ? <MdExpandLess size={20} color="#6b7280" />
                  : <MdExpandMore size={20} color="#6b7280" />}
              </div>
            </div>
            {expandedId === item.id && (
              <div className="checklist-card-answers" onClick={(e) => e.stopPropagation()}>
                {renderAnswers(item.id)}
              </div>
            )}
          </div>
        ))}
      </div>
    );
  };

  return (
    <div className="checklist-container">
      {/* Mobile header */}
      <div className="checklist-mobile-header">
        <IoArrowUndoOutline
          color="black"
          size={22}
          style={{ cursor: 'pointer', flexShrink: 0 }}
          onClick={() => navigate(-1)}
        />
        <h1>Checklist</h1>
      </div>

      {/* Desktop header */}
      <div className="header">
        <button className="back-button" onClick={() => navigate(-1)}>
          <img src={backIcon} alt="Back" style={{ width: 20, height: 20 }} />
        </button>
        <h2 className="page-title">Checklist</h2>
      </div>

      {renderContent()}

      <MobileFooterNav />
    </div>
  );
};

export default Checklist;
